import { useEffect, useRef, useState } from 'react'
import type { Recording, Segment } from './types'
import { archiveFallback } from '@vod/core/playback'
import { isRealtimeRecording } from '@vod/core/voice'
import { locate, mediaToWall, nextSegment, wallToMedia } from './time'

export interface Slot {
  segment: Segment
  utc: number
}

export function useArchivePlayer(
  segments: Segment[],
  onClock: (utc: number) => void,
  onNotice: (message: string) => void,
  onPreviousBoundary?: (before: number) => void,
  audioSegments: Segment[] = [],
) {
  const videos = useRef<(HTMLVideoElement | null)[]>([null, null])
  const audio = useRef<HTMLAudioElement | null>(null)
  const [slots, setSlots] = useState<(Slot | null)[]>([null, null])
  const slotsRef = useRef(slots)
  const [audioSlot, setAudioSlot] = useState<Slot | null>(null)
  const audioSlotRef = useRef<Slot | null>(null)
  const [active, setActive] = useState(0)
  const activeRef = useRef(0)
  const segmentRef = useRef<Segment | null>(null)
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  const audioSegmentsRef = useRef(audioSegments)
  audioSegmentsRef.current = audioSegments
  const clockCallback = useRef(onClock)
  clockCallback.current = onClock
  const noticeCallback = useRef(onNotice)
  noticeCallback.current = onNotice
  const wantPlay = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [buffering, setBuffering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [muted, setMuted] = useState(true)
  const [rate, setRate] = useState(1)
  const preferences = useRef({ muted, rate })
  preferences.current = { muted, rate }
  const [warm, setWarm] = useState(false)
  const [buffered, setBuffered] = useState<[number, number][]>([])
  const lastClock = useRef(0)
  const transitioning = useRef(false)
  const fallbacks = useRef(new Map<string, Recording>())
  const sourceKey = (r: Recording) => `${r.id}:${r.videoUrl}`
  const resolveSegment = (segment: Segment): Segment => ({
    ...segment,
    recording: fallbacks.current.get(sourceKey(segment.recording)) ?? segment.recording,
  })

  function fallback(index: number) {
    const slot = slotsRef.current[index]
    if (!slot || !expectedSource(index)) return false
    const recording = archiveFallback(slot.segment.recording)
    if (!recording) return false
    fallbacks.current.set(sourceKey(slot.segment.recording), recording)
    const segment = { ...slot.segment, recording }
    const next = [...slotsRef.current]
    next[index] = {
      segment,
      utc: index === activeRef.current ? lastClock.current : slot.utc,
    }
    if (index === activeRef.current) {
      segmentRef.current = segment
      setError(null)
      setBuffered([])
      setBuffering(true)
      setWarm(false)
      noticeCallback.current(
        'Playing the cloud archive because the original could not be loaded.',
      )
    }
    commit(next)
    return true
  }

  function commit(next: (Slot | null)[]) {
    slotsRef.current = next
    setSlots(next)
  }
  function expectedSource(index: number) {
    const slot = slotsRef.current[index],
      video = videos.current[index]
    return Boolean(
      slot &&
      video?.currentSrc === new URL(slot.segment.recording.videoUrl, location.href).href,
    )
  }
  function expectedAudioSource() {
    const slot = audioSlotRef.current,
      element = audio.current
    return Boolean(
      slot &&
      element?.currentSrc ===
        new URL(slot.segment.recording.videoUrl, location.href).href,
    )
  }
  function fallbackAudio() {
    const slot = audioSlotRef.current
    if (!slot || !expectedAudioSource()) return false
    const recording = archiveFallback(slot.segment.recording)
    if (!recording) return false
    fallbacks.current.set(sourceKey(slot.segment.recording), recording)
    const nextSlot = { segment: { ...slot.segment, recording }, utc: lastClock.current }
    audioSlotRef.current = nextSlot
    setAudioSlot(nextSlot)
    return true
  }
  function applyAudioState(utc: number, shouldPlay: boolean) {
    const element = audio.current,
      slot = audioSlotRef.current
    if (!element || !slot || !expectedAudioSource()) return
    const duration = Number.isFinite(element.duration) ? element.duration : Infinity
    const target = Math.max(
      0,
      Math.min(wallToMedia(slot.segment.recording, utc), duration - 0.01),
    )
    if (
      element.readyState >= 1 &&
      Number.isFinite(target) &&
      Math.abs(element.currentTime - target) > 0.25
    ) {
      element.currentTime = target
    }
    element.playbackRate = preferences.current.rate
    element.muted = preferences.current.muted
    if (shouldPlay) {
      if (element.paused) {
        const requested = slot.segment.recording.videoUrl
        void element.play().catch((e: DOMException) => {
          if (e.name === 'AbortError') return
          if (audioSlotRef.current?.segment.recording.videoUrl !== requested) return
          if (e.name === 'NotSupportedError' && fallbackAudio()) return
        })
      }
    } else if (!element.paused) {
      element.pause()
    }
  }
  function syncAudio(utc: number, shouldPlay = wantPlay.current) {
    const current = segmentRef.current
    const rawMatch =
      current && isRealtimeRecording(current.recording)
        ? (audioSegmentsRef.current.find((s) => utc >= s.start && utc < s.end) ?? null)
        : null
    const match = rawMatch ? resolveSegment(rawMatch) : null
    if (!match) {
      if (audioSlotRef.current) {
        audio.current?.pause()
        audioSlotRef.current = null
        setAudioSlot(null)
      }
      const activeVideo = videos.current[activeRef.current]
      if (activeVideo) activeVideo.muted = preferences.current.muted
      return
    }
    const activeVideo = videos.current[activeRef.current]
    if (activeVideo) activeVideo.muted = true
    const changed =
      audioSlotRef.current?.segment.recording.id !== match.recording.id ||
      audioSlotRef.current?.segment.recording.videoUrl !== match.recording.videoUrl
    audioSlotRef.current = { segment: match, utc }
    if (changed) setAudioSlot({ segment: match, utc })
    if (expectedAudioSource() && (audio.current?.readyState ?? 0) >= 1) {
      applyAudioState(utc, shouldPlay)
    }
  }
  function play(index: number) {
    const video = videos.current[index]
    if (!video) return
    const requestedSource = slotsRef.current[index]?.segment.recording.videoUrl
    void video.play().catch((e: DOMException) => {
      if (index !== activeRef.current || e.name === 'AbortError') return
      if (slotsRef.current[index]?.segment.recording.videoUrl !== requestedSource) return
      if (e.name === 'NotSupportedError' && fallback(index)) return
      wantPlay.current = false
      audio.current?.pause()
      setPlaying(false)
      if (e.name === 'NotAllowedError') noticeCallback.current('Press play to continue.')
    })
  }
  function ready(index: number) {
    const video = videos.current[index],
      slot = slotsRef.current[index]
    if (!video || !slot || !expectedSource(index)) return
    const target = Math.max(
      0,
      Math.min(wallToMedia(slot.segment.recording, slot.utc), video.duration - 0.01),
    )
    if (Number.isFinite(target)) {
      // Prefer a nearby keyframe for faster seeks when the browser supports it.
      if (
        slot.segment.recording.playbackKind !== 'archive' &&
        typeof video.fastSeek === 'function'
      )
        video.fastSeek(target)
      else video.currentTime = target
    }
    video.playbackRate = preferences.current.rate
    video.muted =
      index === activeRef.current && !audioSlotRef.current
        ? preferences.current.muted
        : true
    if (index === activeRef.current && wantPlay.current) {
      play(index)
      syncAudio(slot.utc, true)
    }
  }
  function activate(segment: Segment, utc: number, autoplay: boolean) {
    segment = resolveSegment(segment)
    const old = activeRef.current
    const match = slotsRef.current.findIndex(
      (s) => s?.segment.recording.id === segment.recording.id,
    )
    const index = match >= 0 ? match : slotsRef.current[old] ? 1 - old : old
    wantPlay.current = autoplay
    activeRef.current = index
    segmentRef.current = segment
    if (old !== index) videos.current[old]?.pause()
    const next = [...slotsRef.current]
    next[index] = { segment, utc }
    commit(next)
    setActive(index)
    setError(null)
    setWarm(false)
    setBuffering(true)
    lastClock.current = utc
    clockCallback.current(utc)
    transitioning.current = false
    syncAudio(utc, false)
    if (expectedSource(index) && (videos.current[index]?.readyState ?? 0) >= 1)
      ready(index)
  }
  function seek(utc: number, autoplay = wantPlay.current) {
    const target = locate(segmentsRef.current, utc)
    if (!target) return
    if (target.gap) noticeCallback.current('Jumped to the nearest available footage.')
    activate(target.segment, target.utc, autoplay)
  }
  const advanceRef = useRef(() => {})
  function advance(minimumGap = 10 * 60000) {
    if (transitioning.current || !segmentRef.current) return
    transitioning.current = true
    const next = nextSegment(segmentsRef.current, segmentRef.current)
    if (!next) {
      videos.current[activeRef.current]?.pause()
      audio.current?.pause()
      wantPlay.current = false
      setPlaying(false)
      setBuffering(false)
      noticeCallback.current('You’ve reached the end of the available footage.')
      return
    }
    const gap = next.start - segmentRef.current.end
    if (gap >= minimumGap)
      noticeCallback.current(`Skipped a ${Math.round(gap / 60000)} minute recording gap.`)
    activate(next, next.start, wantPlay.current)
  }
  advanceRef.current = advance

  // A second player prepares the next clip. It never plays audio while hidden.
  useEffect(() => {
    const current = segmentRef.current
    if (!current) return
    const replacement = segments.find(
      (s) =>
        s.recording.id === current.recording.id &&
        lastClock.current >= s.start &&
        lastClock.current < s.end,
    )
    if (replacement) {
      const resolved = resolveSegment(replacement)
      const loaded = slotsRef.current[activeRef.current]?.segment.recording
      const updated = resolved.recording
      if (
        loaded &&
        (loaded.videoUrl !== updated.videoUrl ||
          loaded.mediaStart !== updated.mediaStart ||
          loaded.mediaEnd !== updated.mediaEnd ||
          loaded.start !== updated.start ||
          loaded.end !== updated.end)
      ) {
        // A query refresh can select an archive while the original is still buffered.
        // Switch the decoder and its mapping together; never interpret old pixels on
        // the replacement rendition's time axis.
        activate(resolved, lastClock.current, wantPlay.current)
        return
      }
      segmentRef.current = resolved
    }
    const following = nextSegment(segments, segmentRef.current!)
    const next = following ? resolveSegment(following) : null
    const index = 1 - activeRef.current
    if (!next) {
      if (slotsRef.current[index]) {
        const copy = [...slotsRef.current]
        copy[index] = null
        commit(copy)
      }
      return
    }
    if (
      slotsRef.current[index]?.segment.recording.id !== next.recording.id ||
      slotsRef.current[index]?.segment.recording.videoUrl !== next.recording.videoUrl ||
      slotsRef.current[index]?.utc !== next.start
    ) {
      const copy = [...slotsRef.current]
      copy[index] = { segment: next, utc: next.start }
      commit(copy)
    }
  }, [segments, active, slots[active]?.segment])

  useEffect(() => {
    if (!segmentRef.current) return
    const activeVideo = videos.current[activeRef.current]
    syncAudio(
      lastClock.current,
      Boolean(
        wantPlay.current &&
        activeVideo &&
        !activeVideo.paused &&
        !activeVideo.seeking &&
        activeVideo.readyState >= 2,
      ),
    )
  }, [audioSegments])

  useEffect(() => {
    videos.current.forEach((video, index) => {
      if (video) {
        video.muted = index === activeRef.current && !audioSlotRef.current ? muted : true
        video.playbackRate = rate
      }
    })
    if (audio.current) {
      audio.current.muted = muted
      audio.current.playbackRate = rate
    }
  }, [muted, rate, active, audioSlot])

  useEffect(() => {
    let frame: number,
      previous = 0
    const tick = (now: number) => {
      frame = requestAnimationFrame(tick)
      const index = activeRef.current,
        video = videos.current[index],
        segment = segmentRef.current
      if (
        !video ||
        !segment ||
        !expectedSource(index) ||
        video.readyState < 2 ||
        video.seeking
      )
        return
      const slot = slotsRef.current[index]
      const utc = Math.min(
        segment.end - 1,
        Math.max(
          segment.start,
          slot?.utc ?? segment.start,
          mediaToWall(segment.recording, video.currentTime),
        ),
      )
      if (
        wantPlay.current &&
        mediaToWall(segment.recording, video.currentTime) >= segment.end - 10
      ) {
        advanceRef.current()
        return
      }
      if (now - previous < 100) return
      previous = now
      if (Math.abs(utc - lastClock.current) > 10) {
        lastClock.current = utc
        clockCallback.current(utc)
      }
      syncAudio(utc, wantPlay.current && !video.paused)
      setWarm(wallToMedia(segment.recording, segment.end) - video.currentTime < 20)
      const ranges: [number, number][] = []
      for (let i = 0; i < video.buffered.length; i++)
        ranges.push([
          mediaToWall(segment.recording, video.buffered.start(i)),
          mediaToWall(segment.recording, video.buffered.end(i)),
        ])
      setBuffered(ranges)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [])

  function clear() {
    fallbacks.current.clear()
    wantPlay.current = false
    for (const video of videos.current) video?.pause()
    audio.current?.pause()
    audioSlotRef.current = null
    setAudioSlot(null)
    commit([null, null])
    segmentRef.current = null
    setPlaying(false)
    setBuffering(false)
    setError(null)
  }

  const activeRecording = slots[active]?.segment.recording ?? null

  return {
    videos,
    audio,
    slots,
    audioSlot,
    externalAudio: Boolean(audioSlot),
    hasAudio: Boolean(
      audioSlot || (activeRecording && activeRecording.hasAudio !== false),
    ),
    active,
    playing,
    buffering,
    error,
    muted,
    rate,
    warm,
    buffered,
    recording: activeRecording,
    seek,
    clear,
    getPlayIntent: () => wantPlay.current,
    toggle() {
      const video = videos.current[activeRef.current]
      if (!video || !segmentRef.current) return
      if (wantPlay.current) {
        wantPlay.current = false
        video.pause()
        audio.current?.pause()
        setPlaying(false)
      } else {
        wantPlay.current = true
        transitioning.current = false
        play(activeRef.current)
        syncAudio(lastClock.current, true)
      }
    },
    next() {
      transitioning.current = false
      advance(60 * 60000)
    },
    previous() {
      const current = segmentRef.current
      if (!current) return
      const video = videos.current[activeRef.current]
      const elapsed =
        (video?.currentTime ?? wallToMedia(current.recording, lastClock.current)) -
        wallToMedia(current.recording, current.start)
      const index = segmentsRef.current.findIndex(
        (segment) =>
          segment.start === current.start &&
          segment.recording.id === current.recording.id,
      )
      if (elapsed < 3 && index === 0 && onPreviousBoundary) {
        onPreviousBoundary(current.start)
        return
      }
      const target = elapsed < 3 && index > 0 ? segmentsRef.current[index - 1] : current
      activate(target, target.start, wantPlay.current)
    },
    retry() {
      setError(null)
      setBuffering(true)
      videos.current[activeRef.current]?.load()
      audio.current?.load()
    },
    setMuted,
    setRate,
    audioHandlers() {
      return {
        onLoadedMetadata: () => {
          const video = videos.current[activeRef.current]
          applyAudioState(
            lastClock.current,
            Boolean(
              wantPlay.current &&
              video &&
              !video.paused &&
              !video.seeking &&
              video.readyState >= 2,
            ),
          )
        },
        onError: () => {
          if (!expectedAudioSource()) return
          fallbackAudio()
        },
      }
    },
    handlers(index: number) {
      return {
        onLoadedMetadata: () => ready(index),
        onCanPlay: () => {
          if (index === activeRef.current) {
            setBuffering(false)
            const video = videos.current[index]
            if (wantPlay.current && video && !video.paused)
              syncAudio(lastClock.current, true)
          }
        },
        onWaiting: () => {
          if (index === activeRef.current) {
            setBuffering(true)
            audio.current?.pause()
          }
        },
        onSeeking: () => {
          if (index === activeRef.current) {
            setBuffering(true)
            audio.current?.pause()
          }
        },
        onSeeked: () => {
          if (index === activeRef.current) {
            setBuffering(false)
            const video = videos.current[index]
            syncAudio(
              lastClock.current,
              Boolean(wantPlay.current && video && !video.paused),
            )
          }
        },
        onPlay: () => {
          if (index === activeRef.current) {
            wantPlay.current = true
            setPlaying(true)
            syncAudio(lastClock.current, true)
          } else videos.current[index]?.pause()
        },
        onPause: () => {
          if (index === activeRef.current && !videos.current[index]?.ended) {
            audio.current?.pause()
            setPlaying(false)
          }
        },
        onEnded: () => {
          if (index === activeRef.current) advanceRef.current()
        },
        onError: () => {
          if (!expectedSource(index)) return
          if (fallback(index)) return
          if (index !== activeRef.current || !slotsRef.current[index]) return
          const hevc = /hevc|hvc1|hev1/i.test(
            slotsRef.current[index]?.segment.recording.codec ?? '',
          )
          setBuffering(false)
          setPlaying(false)
          setError(
            hevc
              ? 'This HEVC recording could not be played. Try Safari or a browser with HEVC support.'
              : 'This recording could not be loaded. It may be unavailable in R2, or unsupported by this browser.',
          )
        },
      }
    },
  }
}
